import * as React from 'react';
import { Disclosure } from '@headlessui/react';
import {
  HiOutlineAdjustmentsHorizontal,
  HiOutlineCircleStack,
  HiOutlineArrowLeft,
  HiOutlineArrowUpRight,
  HiOutlineChartBar,
  HiOutlineChevronDown,
  HiOutlineClock,
  HiOutlineCursorArrowRays,
  HiOutlineGlobeAlt,
  HiOutlineSquares2X2,
} from 'react-icons/hi2';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useMarketClockMs } from 'hooks/useMarketSessionClock';
import { useMarketCalendar } from 'context/MarketCalendarContext';
import resolveUserTimezone from 'utils/shared/time/resolveUserTimezone';
import {
  formatFlowAnalysisWindow,
  getFlowAnalysisSchedule,
} from 'layouts/FlowPageContainer/flowAiAnalysisSchedule';
import type { FlowAnalysisReport } from 'constants/payloads/flowAnalysis';
import {
  FlowAiAnalysisReportExample,
  FlowAnalysisSectionId,
} from 'layouts/FlowPageContainer/FlowAiAnalysisReport';

interface Props {
  id: string;
  onBack: () => void;
  headingRef: React.RefObject<HTMLHeadingElement>;
  report: FlowAnalysisReport | null;
  availableSections: string[];
  onViewSection: (id: FlowAnalysisSectionId) => void;
}

interface Cell {
  label: string;
  meaning: string;
}

interface ReportSection {
  id: FlowAnalysisSectionId;
  title: string;
  eyebrow: string;
  benefit: string;
  cue: string;
  /** A field the report prints as one word, shown as cells instead of prose. */
  cells?: { caption: string; items: Cell[]; note?: string };
  fields: { label: string; meaning: string }[];
  action: string;
  icon: typeof HiOutlineGlobeAlt;
}

// Every string here is a claim or an instruction. The guard in flowAiAnalysisCopyGuard.test.ts
// holds the rules: no dash, no hedge, no definition by negation, at most two sentences each, and
// the report's own labels stay present so the guide always matches what the report prints.
// Fit: at the 896px desktop modal, a verdict cell is one line, definitions side by side end
// together, and no line ends with a word alone. A `\u00a0` (or `&nbsp;` in JSX) binds the last
// two words where a lone word would wrap. scripts/ai-analysis-guide-harness checks all three.
const sections: ReportSection[] = [
  {
    id: 'market-trends',
    title: 'Market Trends',
    eyebrow: 'The big picture',
    benefit: 'The themes the money is backing right now.',
    cue: 'Flow, price, sector breadth, and catalysts, read as one story.',
    cells: {
      caption: 'Stage',
      items: [
        { label: 'Emerging', meaning: 'A theme is forming.' },
        { label: 'Established', meaning: 'The money keeps backing it.' },
        { label: 'Fading', meaning: 'Support is leaving.' },
      ],
    },
    fields: [
      {
        label: 'Headline',
        meaning: 'The theme in one line: which sector or story the money is\u00a0behind.',
      },
      {
        label: 'Outlook & Strength',
        meaning:
          'Bullish or bearish, and how hard the evidence agrees. At 5, the evidence is stacked on one side with nothing against it.',
      },
      {
        label: 'Why',
        meaning:
          'The proof: the flow, the price action, the breadth, and the catalyst that put the theme on the board.',
      },
      {
        label: 'Across updates',
        meaning:
          'Themes carry from report to report while the money keeps backing them. When support leaves, they leave the board.',
      },
      {
        label: 'First Detected',
        meaning:
          'When the AI first called the theme, Eastern time. The earlier the stamp, the longer the theme has held.',
      },
    ],
    action: 'Build your watchlist from the Established themes. Watch Emerging for the next one.',
    icon: HiOutlineGlobeAlt,
  },
  {
    id: 'hourly-insights',
    title: 'Hourly Insights',
    eyebrow: 'This hour',
    benefit: 'Catch the turn the hour it happens.',
    cue: 'One word tells you if this hour confirms the trend or fights it.',
    cells: {
      caption: 'Hourly Read',
      items: [
        { label: 'Confirming', meaning: 'This hour backs the trend.' },
        { label: 'Contradicting', meaning: 'This hour fights the trend.' },
        { label: 'Mixed', meaning: 'The hour is split.' },
      ],
      note: 'The verdict on the hour, then the tape behind it: the sweeps, clusters, and premium that moved in the last 60 minutes.',
    },
    fields: [
      {
        label: 'Outlook & Strength',
        meaning:
          'Direction for the hour and how hard the evidence agrees. A strong Contradicting read is the earliest look at a turn.',
      },
      {
        label: 'Why',
        meaning: 'What drove the hour: the flow, the price move, and the\u00a0catalyst.',
      },
    ],
    action: 'Contradicting reads print first. That is where the turns start.',
    icon: HiOutlineChartBar,
  },
  {
    id: 'noteworthy-trades',
    title: 'Noteworthy Trades',
    eyebrow: 'The tape',
    benefit: 'The biggest, most aggressive prints on the tape, decoded.',
    cue: 'Contract, premium, timing, and the intent behind the money.',
    fields: [
      {
        label: 'Contract',
        meaning: 'Ticker, strike, call or put, expiry. Tap the ticker to open its live\u00a0flow.',
      },
      {
        label: 'Premium & time',
        meaning:
          'Premium is the dollars committed on the print. The time stamp is the moment it hit the tape, Eastern.',
      },
      {
        label: 'Activity badge',
        meaning:
          'Sweep, Golden Sweep, Above Ask, Top Position. The badge is the tell: urgency, size, and new money.',
      },
      {
        label: 'Intent & Strength',
        meaning:
          'What the money is doing: Positioning, Hedge, Short-Term, Roll, or Stock Replacement. Strength is the conviction.',
      },
      {
        label: 'Why',
        meaning: 'The case: the print, the structure, and the backdrop.',
      },
    ],
    action: 'Tap the ticker. See the rest of the tape in that name.',
    icon: HiOutlineCursorArrowRays,
  },
  {
    id: 'options-plays',
    title: 'Options Plays',
    eyebrow: 'The trade',
    benefit: 'Setups built from the flow, ready for the calculator.',
    cue: 'Strategy, strikes, expiry. One tap into the calculator.',
    fields: [
      {
        label: 'Linked play',
        meaning: 'Tap the play. The calculator opens with the strategy and ticker already loaded.',
      },
      {
        label: 'Outlook & Strength',
        meaning:
          'Direction and conviction. At 5, the evidence is stacked behind the trade with nothing against it.',
      },
      {
        label: 'Strategy',
        meaning:
          'The structure, the strikes to target, the expiry window, and the catalyst that sets the clock.',
      },
      {
        label: 'Why',
        meaning: 'Why this trade, now: the flow, the backdrop, and the catalyst behind it.',
      },
    ],
    action: 'Open a play, set your strikes, and see the payoff before you commit a dollar.',
    icon: HiOutlineSquares2X2,
  },
];

interface SectionProps {
  section: ReportSection;
  index: number;
  report: FlowAnalysisReport | null;
  canViewSection: boolean;
  onViewSection: Props['onViewSection'];
}

function SectionGuide({ section, index, report, canViewSection, onViewSection }: SectionProps) {
  // Name the control by its title; the benefit and cue stay available as its description.
  const id = React.useId();
  return (
    <Disclosure as="article" className="contents">
      {({ open }) => (
        <>
          <Disclosure.Button
            aria-labelledby={`${id}-title`}
            aria-describedby={`${id}-benefit ${id}-cue`}
            className={`group w-full rounded-xl border px-[18px] py-4 text-left transition-colors hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue sm:row-start-1 ${
              index % 2 === 0 ? 'sm:col-start-1' : 'sm:col-start-2'
            } ${
              open ? 'border-white/20 bg-white/[0.04]' : 'border-palette-border bg-palette-black-4'
            }`}
          >
            <span className="flex items-center gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-palette-border bg-palette-black-3">
                <section.icon aria-hidden="true" className="h-[18px] w-[18px] text-call-text" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] font-semibold uppercase leading-4 tracking-[0.14em] text-palette-black-7">
                  {section.eyebrow}
                </span>
                <span
                  id={`${id}-title`}
                  className="block text-[17px] font-semibold leading-6 text-white"
                >
                  {section.title}
                </span>
              </span>
              <span aria-hidden="true" className="font-mono text-[10px] text-white/30">
                0{index + 1}
              </span>
              <HiOutlineChevronDown
                aria-hidden="true"
                className={`h-4 w-4 shrink-0 text-text-secondary transition-transform motion-reduce:transition-none ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </span>
            <span
              id={`${id}-benefit`}
              className="mt-2 block text-[13px] font-medium leading-5 text-white"
            >
              {section.benefit}
            </span>
            <span id={`${id}-cue`} className="mt-0.5 block text-xs leading-5 text-text-secondary">
              {section.cue}
            </span>
          </Disclosure.Button>
          <Disclosure.Panel
            role="region"
            aria-labelledby={`${id}-title`}
            className="rounded-xl border border-palette-border bg-palette-black-2 p-4 text-[13px] leading-6 text-text-secondary sm:col-span-2 sm:p-5"
          >
            {report && <FlowAiAnalysisReportExample report={report} sectionId={section.id} />}
            <div className="mb-5 flex flex-col items-start gap-3 border-b border-palette-border pb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
              <p className="flex items-start gap-2 text-[13px] font-semibold leading-5 text-white">
                <HiOutlineArrowUpRight
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-call-text"
                />
                {section.action}
              </p>
              {canViewSection && (
                <button
                  type="button"
                  onClick={() => onViewSection(section.id)}
                  className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-lg bg-brand-primary px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-button-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue focus-visible:ring-offset-2 focus-visible:ring-offset-palette-black-2"
                >
                  View {section.title}
                  <HiOutlineArrowUpRight aria-hidden="true" className="h-4 w-4" />
                </button>
              )}
            </div>
            {section.cells && (
              <div className="mb-4">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-secondary">
                  {section.cells.caption}
                </p>
                <div className="grid gap-3 sm:grid-cols-3">
                  {section.cells.items.map(({ label, meaning }) => (
                    <div key={label} className="rounded-lg bg-palette-black-3 px-3 py-2.5">
                      <p className="mb-1 inline-flex rounded-full border border-palette-border bg-palette-black-4 px-2 py-0.5 text-[11px] font-semibold leading-4 text-white">
                        {label}
                      </p>
                      <p className="text-xs leading-5 text-text-secondary">{meaning}</p>
                    </div>
                  ))}
                </div>
                {section.cells.note && <p className="mt-3">{section.cells.note}</p>}
              </div>
            )}
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {section.fields.map(({ label, meaning }, fieldIndex) => (
                <div
                  key={label}
                  className={
                    section.fields.length % 2 === 1 && fieldIndex === section.fields.length - 1
                      ? 'sm:col-span-2'
                      : undefined
                  }
                >
                  <dt className="font-semibold text-white">{label}</dt>
                  <dd>{meaning}</dd>
                </div>
              ))}
            </dl>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

interface DetailProps {
  title: string;
  summary: string;
  Icon: typeof HiOutlineGlobeAlt;
  children: React.ReactNode;
}

function GuideDetail({ title, summary, Icon, children }: DetailProps) {
  return (
    <Disclosure as="div" className="border-b border-palette-border last:border-b-0">
      {({ open }) => (
        <>
          <Disclosure.Button className="flex min-h-[52px] w-full items-center justify-between gap-3 rounded-lg py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue">
            <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-call-text" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-white">{title}</span>
              <span className="block text-[11px] leading-4 text-text-secondary">{summary}</span>
            </span>
            <HiOutlineChevronDown
              aria-hidden="true"
              className={`h-4 w-4 shrink-0 text-text-secondary transition-transform motion-reduce:transition-none ${
                open ? 'rotate-180' : ''
              }`}
            />
          </Disclosure.Button>
          <Disclosure.Panel className="pb-6 text-sm leading-6 text-text-secondary">
            {children}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

/** The 1 to 5 scale, drawn: five dots that brighten toward 5. Neutral, because Strength has no direction. */
function StrengthMeter() {
  return (
    <span aria-hidden="true" className="ml-2 inline-flex items-center gap-1.5 align-middle">
      {['opacity-30', 'opacity-50', 'opacity-60', 'opacity-80', 'opacity-100'].map((opacity) => (
        <span key={opacity} className={`h-2 w-2 rounded-full bg-white ${opacity}`} />
      ))}
    </span>
  );
}

export default function FlowAiAnalysisGuide({
  id,
  onBack,
  headingRef,
  report,
  availableSections,
  onViewSection,
}: Props) {
  const { user } = useCurrentUser();
  const nowMs = useMarketClockMs();
  useMarketCalendar();
  const zone = resolveUserTimezone(user?.timezoneLocation);
  const schedule = nowMs === null ? null : getFlowAnalysisSchedule(nowMs);

  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain text-pretty bg-palette-black-3 [color-scheme:dark] [scrollbar-width:thin]"
      data-analysis-guide
    >
      <div className="sticky top-0 z-10 border-b border-palette-border bg-palette-black-3/95 px-5 py-1 backdrop-blur-sm sm:px-8">
        <button
          type="button"
          onClick={onBack}
          className="-ml-2 inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2 text-xs font-medium text-text-secondary transition-colors hover:bg-white/[0.05] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
        >
          <HiOutlineArrowLeft aria-hidden="true" className="h-4 w-4" />
          Back to analysis
        </button>
      </div>
      <div className="px-5 pb-4 pt-3 sm:px-8">
        <div className="rounded-xl border border-palette-border bg-palette-black-2 px-5 py-4 sm:px-6">
          <p className="mb-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-palette-black-7">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-palette-black-6" />
            Your guide to AI Market Analysis
          </p>
          <h3
            id={`${id}-heading`}
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-bold leading-tight text-white outline-none sm:text-[26px]"
          >
            Where the smart money moved this hour.
            <br className="hidden sm:block" /> And how to trade it.
          </h3>
          <p className="mt-1.5 max-w-2xl text-xs leading-5 text-text-secondary">
            Every hour the AI reads the options tape, the dark pools, price action, and the news.
            You get the themes in play, the trades that matter, and setups ready for the calculator.
          </p>
          <div className="mt-2.5 hidden flex-wrap gap-2 sm:flex">
            {[
              'Options flow',
              'Dark pools',
              'Price & sectors',
              'Catalysts',
              'Hourly, daily, 5-day',
            ].map((label) => (
              <span
                key={label}
                className="rounded-full bg-palette-black-3 px-2.5 py-1 text-[11px] font-medium text-text-secondary"
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-2 mt-3">
          <h4 className="text-base font-semibold text-white">Four sections. One market read.</h4>
        </div>
        <div className="space-y-3">
          {[0, 2].map((start) => (
            <div
              key={start}
              className="grid items-start gap-3 sm:grid-cols-2"
              data-guide-section-pair
            >
              {sections.slice(start, start + 2).map((section, offset) => (
                <SectionGuide
                  key={section.id}
                  section={section}
                  index={start + offset}
                  report={report}
                  canViewSection={availableSections.includes(section.id)}
                  onViewSection={onViewSection}
                />
              ))}
            </div>
          ))}
        </div>

        <div className="mt-3 rounded-lg border border-palette-border bg-palette-black-4 px-5 sm:px-6">
          <GuideDetail
            Icon={HiOutlineAdjustmentsHorizontal}
            title="How to read the signals"
            summary="Outlook, Strength, Why, and Intent in one minute"
          >
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="font-semibold text-white">Outlook</dt>
                <dd>Bullish, bearish, or neutral. The direction the money is&nbsp;leaning.</dd>
              </div>
              <div>
                <dt className="font-semibold text-white">
                  Strength
                  <StrengthMeter />
                </dt>
                <dd>
                  How hard the evidence agrees, 1 to 5. At 5, the evidence is stacked on one side
                  with nothing against it.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Why</dt>
                <dd>
                  The proof behind every call: the flow, the price, the breadth, and the catalyst.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Intent</dt>
                <dd>
                  What the money is doing: Positioning, Hedge, Short-Term, Roll, or Stock
                  Replacement.
                </dd>
              </div>
            </dl>
            <p className="mt-4 border-l-2 border-palette-black-6 pl-3 text-white">
              Every item has to earn its place. &quot;No high-strength items&quot; means nothing
              there earned your capital this hour.
            </p>
          </GuideDetail>

          <GuideDetail
            Icon={HiOutlineCircleStack}
            title="What the AI reads every hour"
            summary="Options flow, dark pools, price action, and catalysts"
          >
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="font-semibold text-white">Options flow</dt>
                <dd>
                  Call and put premium, ask-side buying, sweeps, golden sweeps, and unusual prints,
                  by strike and expiry.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Price & breadth</dt>
                <dd>
                  Stock and sector moves, the names moving together, and ETF flow. Breadth confirms
                  a sector-wide&nbsp;move.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Catalysts</dt>
                <dd>
                  Economic data, rates and policy, earnings, and company news, tied to the
                  positioning they explain.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Three clocks</dt>
                <dd>
                  This hour, today, and the past five sessions. A theme that holds on all three has
                  the money behind it.
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="font-semibold text-white">Dark pools & exchanges</dt>
                <dd>
                  Institutional accumulation and distribution on the stock tape, matched against the
                  options flow. When both agree, conviction goes up.
                </dd>
              </div>
            </dl>
          </GuideDetail>

          <GuideDetail
            Icon={HiOutlineClock}
            title="When it lands"
            summary="Every hour of the trading day, at :30."
          >
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-palette-border bg-palette-black-3 p-4">
              <HiOutlineClock
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0 text-call-text"
              />
              <div>
                <p className="font-semibold text-white">Every hour at :30, on US trading days</p>
                <p className="mt-1 text-xs leading-5 text-text-secondary">
                  10:30 AM to 4:30 PM Eastern. Seven reads on a full trading day.
                </p>
                {schedule && zone !== 'America/New_York' && (
                  <p className="mt-1 text-xs leading-5 text-white">
                    In your timezone: {formatFlowAnalysisWindow(schedule.scheduleDate, zone)}
                  </p>
                )}
              </div>
            </div>
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="font-semibold text-white">Updated</dt>
                <dd>When this read was published.</dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Next ~</dt>
                <dd>When the next read lands.</dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Weekends & holidays</dt>
                <dd>
                  The next read is the next trading day&apos;s first, 10:30&nbsp;AM&nbsp;Eastern.
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-white">Timezones</dt>
                <dd>
                  Header times follow your timezone setting. Times inside the report are Eastern.
                </dd>
              </div>
            </dl>
            <p className="mt-4">
              Open it any time. The latest read is always waiting, and New on the button means a
              fresh read landed since you last opened it.
            </p>
          </GuideDetail>
        </div>
      </div>
    </section>
  );
}
